import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Accounting = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState({ revenue: 0, expenses: 0, profit: 0 });
  const [loading, setLoading] = useState(true);
  
  // Line Items state
  const [lineItems, setLineItems] = useState([]);
  const [lineItemLoading, setLineItemLoading] = useState(false);
  const [showLineItemModal, setShowLineItemModal] = useState(false);
  const [editingLineItem, setEditingLineItem] = useState(null);
  const [lineItemForm, setLineItemForm] = useState({ description: '', quantity: 1, unit_price: 0 });

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    if (activeTab === 'line-items') {
      fetchLineItems();
    }
  }, [activeTab]);

  const fetchSummary = async () => {
    try {
      const res = await fetch('/api/reports/summary');
      const data = await res.json();
      setSummary({
        revenue: data.totalRevenue || 0,
        expenses: 0,
        profit: (data.totalRevenue || 0) - 0
      });
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLineItems = async () => {
    setLineItemLoading(true);
    try {
      const res = await fetch('/api/line-items');
      const data = await res.json();
      setLineItems(data.lineItems || []);
    } catch (err) {
      console.error('Failed to fetch line items:', err);
    } finally {
      setLineItemLoading(false);
    }
  };

  const handleSaveLineItem = async () => {
    try {
      const url = editingLineItem ? `/api/line-items/${editingLineItem.id}` : '/api/line-items';
      const method = editingLineItem ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(lineItemForm)
      });
      
      if (res.ok) {
        setShowLineItemModal(false);
        setEditingLineItem(null);
        setLineItemForm({ description: '', quantity: 1, unit_price: 0 });
        fetchLineItems();
      } else {
        alert('Failed to save line item');
      }
    } catch (err) {
      console.error('Failed to save line item:', err);
    }
  };

  const handleEditLineItem = (item) => {
    setEditingLineItem(item);
    setLineItemForm({ description: item.description, quantity: item.quantity, unit_price: item.unit_price });
    setShowLineItemModal(true);
  };

  const handleDeleteLineItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this line item?')) return;
    try {
      const res = await fetch(`/api/line-items/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchLineItems();
      } else {
        alert('Failed to delete line item');
      }
    } catch (err) {
      console.error('Failed to delete line item:', err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (!isAdmin) {
    return (
      <div className="page">
        <Link to="/" className="btn btn-secondary mb-4">← Back to Dashboard</Link>
        <h1>🚫 Access Denied</h1>
        <p>You must be an admin to access Accounting.</p>
      </div>
    );
  }

  return (
    <div className="page">
      <Link to="/" className="btn btn-secondary mb-4">← Back to Dashboard</Link>
      <div className="page-header">
        <h1>📊 Accounting</h1>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Invoices
        </button>
        <button 
          className={`tab ${activeTab === 'expenses' ? 'active' : ''}`}
          onClick={() => setActiveTab('expenses')}
        >
          Expenses
        </button>
        <button 
          className={`tab ${activeTab === 'taxes' ? 'active' : ''}`}
          onClick={() => setActiveTab('taxes')}
        >
          Taxes
        </button>
        <button 
          className={`tab ${activeTab === 'line-items' ? 'active' : ''}`}
          onClick={() => setActiveTab('line-items')}
        >
          Line Items
        </button>
        <button 
          className={`tab ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          Reports
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="tab-content">
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Revenue (YTD)</h3>
              <p className="stat-value">${summary.revenue.toLocaleString()}</p>
            </div>
            <div className="stat-card">
              <h3>Expenses (YTD)</h3>
              <p className="stat-value">${summary.expenses.toLocaleString()}</p>
            </div>
            <div className="stat-card">
              <h3>Net Profit</h3>
              <p className="stat-value" style={{ color: summary.profit >= 0 ? 'green' : 'red' }}>
                ${summary.profit.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="card mt-4">
            <h3>Quick Actions</h3>
            <div className="button-grid">
              <button className="btn btn-primary" onClick={() => window.location.href = '/invoices'}>Create Invoice</button>
              <button className="btn btn-secondary" onClick={() => alert('Expense recording coming soon')}>Record Expense</button>
              <button className="btn btn-secondary" onClick={() => alert('Report generation coming soon')}>Generate Report</button>
              <button className="btn btn-secondary" onClick={() => alert('QuickBooks integration coming soon')}>Export to QuickBooks</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'invoices' && (
        <div className="tab-content">
          <div className="card">
            <h3>Invoice Management</h3>
            <p>Manage all customer invoices and payments.</p>
            <a href="/invoices" className="btn btn-primary">Go to Invoices →</a>
          </div>
        </div>
      )}

      {activeTab === 'expenses' && (
        <div className="tab-content">
          <div className="card">
            <h3>Expense Tracking</h3>
            <p>Track business expenses and categorize them for tax purposes.</p>
            <button className="btn btn-primary">+ Add Expense</button>
          </div>
        </div>
      )}

      {activeTab === 'taxes' && (
        <div className="tab-content">
          <div className="card">
            <h3>Tax Summary</h3>
            <p>View tax liability and generate tax reports.</p>
            <div className="info-box">
              <p><strong>Estimated Tax Liability:</strong> ${((summary.profit * 0.25) || 0).toLocaleString()}</p>
              <p><small>Based on 25% corporate tax rate estimate</small></p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'line-items' && (
        <div className="tab-content">
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3>Custom Line Items</h3>
              <button 
                className="btn btn-primary"
                onClick={() => { setEditingLineItem(null); setLineItemForm({ description: '', quantity: 1, unit_price: 0 }); setShowLineItemModal(true); }}
              >
                + Add Line Item
              </button>
            </div>
            <p style={{ marginBottom: 20 }}>Create reusable line items to quickly add to invoices.</p>
            
            {lineItemLoading ? (
              <p>Loading...</p>
            ) : lineItems.length === 0 ? (
              <p style={{ textAlign: 'center', padding: 40, color: '#888' }}>No line items yet. Click "Add Line Item" to create one.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map(item => (
                    <tr key={item.id}>
                      <td>{item.description}</td>
                      <td>{item.quantity}</td>
                      <td>{formatCurrency(item.unit_price)}</td>
                      <td>{formatCurrency(item.total)}</td>
                      <td>
                        <button className="btn btn-secondary btn-sm" onClick={() => handleEditLineItem(item)}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteLineItem(item.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="tab-content">
          <div className="card">
            <h3>Financial Reports</h3>
            <div className="report-list">
              <button className="btn btn-secondary mb-2">Profit & Loss Statement</button>
              <button className="btn btn-secondary mb-2">Balance Sheet</button>
              <button className="btn btn-secondary mb-2">Cash Flow Statement</button>
              <button className="btn btn-secondary mb-2">Sales by Customer</button>
            </div>
          </div>
        </div>
      )}

      {/* Line Item Modal */}
      {showLineItemModal && (
        <div className="modal-overlay" onClick={() => setShowLineItemModal(false)}>
          <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingLineItem ? 'Edit Line Item' : 'Add Line Item'}</h2>
              <button className="modal-close" onClick={() => setShowLineItemModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-input"
                  value={lineItemForm.description}
                  onChange={(e) => setLineItemForm({ ...lineItemForm, description: e.target.value })}
                  placeholder="e.g., Remote Support - 1 Hour"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Quantity</label>
                  <input
                    type="number"
                    className="form-input"
                    value={lineItemForm.quantity}
                    onChange={(e) => setLineItemForm({ ...lineItemForm, quantity: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Unit Price</label>
                  <input
                    type="number"
                    className="form-input"
                    value={lineItemForm.unit_price}
                    onChange={(e) => setLineItemForm({ ...lineItemForm, unit_price: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div style={{ marginTop: 15, textAlign: 'right' }}>
                <strong>Total: {formatCurrency((lineItemForm.quantity || 0) * (lineItemForm.unit_price || 0))}</strong>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowLineItemModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveLineItem} disabled={!lineItemForm.description}>
                {editingLineItem ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounting;
